import { useState, useCallback, useEffect, useRef } from 'react';
import {
  BudgetLine, ManualMatchMap,
  DEFAULT_BUDGET_LINES,
  budgetLineKey,
} from '../data/budgetData';
import {
  fetchBudget, fetchManualMatch, putBudgetLine,
  importBudgetLines, putManualMatch, rowsToOverrides,
  migrateLocalStorageToCloud,
} from '../lib/budgetApi';
import { getCurrentUser } from '../lib/user';

function applyOverrides(defaults: BudgetLine[], overrides: Record<string, any>): BudgetLine[] {
  const map = new Map<string, BudgetLine>();
  defaults.forEach(line => {
    map.set(budgetLineKey(line), line);
  });

  Object.entries(overrides).forEach(([key, ov]) => {
    const existing = map.get(key);
    if (existing) {
      map.set(key, { ...existing, amount: ov.amount, isLocked: ov.isLocked });
    } else if (ov && ov.noAcc && ov.keterangan) {
      map.set(key, ov as BudgetLine);
    }
  });

  return Array.from(map.values());
}

interface UseBudgetOptions {
  entity?: string;
  fiscalYear?: number;
}

export function useBudget(opts: UseBudgetOptions = {}) {
  const entity = opts.entity ?? 'SAHABAT_GADAI';
  const fiscalYear = opts.fiscalYear ?? new Date().getFullYear();

  const [overrides, setOverrides] = useState<Record<string, any>>({});
  const [manualMap, setManualMapState] = useState<ManualMatchMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const migrationRan = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        let rows = await fetchBudget(entity, fiscalYear);
        if (rows.length === 0 && !migrationRan.current) {
          migrationRan.current = true;
          const result = await migrateLocalStorageToCloud(entity, fiscalYear, getCurrentUser());
          if (result) {
            console.log(`[budget] Migrated ${result.migrated} lines from localStorage to cloud`);
            rows = await fetchBudget(entity, fiscalYear);
          }
        }
        const mm = await fetchManualMatch(entity);
        if (cancelled) return;
        setOverrides(rowsToOverrides(rows));
        setManualMapState(mm);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'fetch error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [entity, fiscalYear]);

  const lines: BudgetLine[] = applyOverrides(DEFAULT_BUDGET_LINES, overrides);

  const updateLine = useCallback(async (noAcc: string, keterangan: string, amount: number, isLocked: boolean) => {
    const key = `${noAcc}__${keterangan}`;
    const prevSnapshot = overrides[key];
    setOverrides(prev => {
      const existing = prev[key];
      const nextVal = existing && (existing as any).noAcc
        ? { ...(existing as any), amount, isLocked }
        : { amount, isLocked };
      return { ...prev, [key]: nextVal };
    });
    try {
      await putBudgetLine({
        entity, fiscal_year: fiscalYear,
        no_acc: noAcc, keterangan,
        amount, is_locked: isLocked,
        updated_by: getCurrentUser(),
      });
      setError(null);
    } catch (e: any) {
      setOverrides(prev => {
        const next = { ...prev };
        if (prevSnapshot === undefined) delete next[key];
        else next[key] = prevSnapshot;
        return next;
      });
      setError(`Save failed: ${e.message}`);
    }
  }, [entity, fiscalYear, overrides]);

  const resetLine = useCallback(async (noAcc: string, keterangan: string) => {
    const key = `${noAcc}__${keterangan}`;
    const defaultLine = DEFAULT_BUDGET_LINES.find(l => budgetLineKey(l) === key);
    if (!defaultLine) return;
    const prevSnapshot = overrides[key];
    setOverrides(prev => ({ ...prev, [key]: { amount: defaultLine.defaultAmount, isLocked: true } }));
    try {
      await putBudgetLine({
        entity, fiscal_year: fiscalYear,
        no_acc: noAcc, keterangan,
        amount: defaultLine.defaultAmount, is_locked: true,
        updated_by: getCurrentUser(),
      });
      setError(null);
    } catch (e: any) {
      setOverrides(prev => {
        const next = { ...prev };
        if (prevSnapshot === undefined) delete next[key];
        else next[key] = prevSnapshot;
        return next;
      });
      setError(`Reset failed: ${e.message}`);
    }
  }, [entity, fiscalYear, overrides]);

  const setManualMatch = useCallback(async (glRowIndex: number, budgetKey: string) => {
    const prevSnapshot = manualMap[String(glRowIndex)];
    setManualMapState(prev => ({ ...prev, [String(glRowIndex)]: budgetKey }));
    try {
      await putManualMatch({
        entity, gl_row_index: glRowIndex, budget_key: budgetKey,
        updated_by: getCurrentUser(),
      });
      setError(null);
    } catch (e: any) {
      setManualMapState(prev => {
        const next = { ...prev };
        if (prevSnapshot === undefined) delete next[String(glRowIndex)];
        else next[String(glRowIndex)] = prevSnapshot;
        return next;
      });
      setError(`Match save failed: ${e.message}`);
    }
  }, [entity, manualMap]);

  const importLines = useCallback(async (imported: BudgetLine[], strategy: 'replace' | 'skip') => {
    setLoading(true);
    try {
      await importBudgetLines({
        entity, fiscal_year: fiscalYear,
        lines: imported, strategy,
        updated_by: getCurrentUser(),
      });
      const rows = await fetchBudget(entity, fiscalYear);
      setOverrides(rowsToOverrides(rows));
      setError(null);
    } catch (e: any) {
      setError(`Import failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [entity, fiscalYear]);

  return { lines, loading, error, updateLine, resetLine, setManualMatch, manualMap, importLines };
}
